import { useQuery } from '@tanstack/react-query';
import { isLinkableTin } from '../lib/tin';
import { useLocale, useT } from '../i18n/locale-context';
import { getBuyerGraph, type SupplierRef } from '../lib/catalog-client';
import { formatBudgetCompact, pluralRuIndex } from '../lib/format';
import { useHydrated } from '../lib/use-hydrated';
import { LocaleLink } from '../i18n/locale-link';
import { AnalyticsTeaser } from './analytics-teaser';
import { ConcentrationTeaserBody } from './analytics-teaser-bodies';
import { countryConfig } from '../country';
import {
  ConcentrationPill,
  MarketShareBar,
  SEGMENT_BG,
  ShareTile,
  type ShareSegment,
} from './analytics-card-parts';
import { InfoHint } from './info-hint';
import {
  concentrationPillLevel,
  concentrationRead,
  hhiTitle,
  concentrationSentence,
  mayAssertConcentration,
} from '../lib/concentration-read';

/**
 * Buyer↔supplier concentration panel for a tender's detail page — how
 * concentrated this buyer's award history is among its suppliers (a plain-words
 * verdict, top-supplier share, single-supplier dependence flag) plus the top supplier
 * breakdown by award value. Twin surface to `BaseRatesPanel`; same
 * fetch/gating/loading/locked pattern.
 *
 * Layout: verdict-first (design pass, brief mockup «01»). The ten repeated
 * per-row progress bars collapsed into ONE distribution bar so the market
 * SHAPE reads at a glance; two stat tiles answer «сосредоточено ли?» before
 * the list; the verdict is plain words, the HHI number only in its hover
 * `title` and only from 10 contracts (`concentrationRead`); supplier names shed
 * their legal-form suffix into a compact chip so the identifying word stops
 * truncating; and the top-5 rows carry a rank dot whose colour matches their
 * segment in the bar, so bar↔list read as one object without a legend.
 *
 * Hide-when-empty (Phase 5): the response carries a non-sensitive `hasData`
 * existence flag. The panel renders NOTHING when there's no data behind the
 * wall (`!hasData`) — no dead upsell on tenders whose buyer has no comparable
 * award history. `locked && hasData` → the locked teaser; unlocked data →
 * the panel. Guests see the teaser too (the endpoint is public and answers them
 * with the locked payload) — it is one of the arguments for registering.
 */

/** How many suppliers get their own coloured segment/row before the rest fold
 *  into the neutral «остальные» tail of the distribution bar. */
const TOP_N = 5;

/** The three Russian plural forms, in the order `pluralRuIndex` returns. */
const PLURAL_FORMS = ['one', 'few', 'many'] as const;

/** Legal-form suffixes (Uzbek Latin + the two RU forms) → compact chip label.
 *  End-anchored and applied only when a non-empty name remains, so a real name
 *  is never mangled. */
const ORG_FORMS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\s*MAS['’`ʼ]?ULIYATI\s+CHEKLANGAN\s+JAMIYATI\s*$/i, 'MChJ'],
  [/\s*AKSIYADORLIK\s+JAMIYATI\s*$/i, 'AJ'],
  [/\s*QO['’`ʼ]?SHMA\s+KORXONA\s*$/i, 'QK'],
  [/\s*XUSUSIY\s+KORXONA\s*$/i, 'XK'],
  [/\s*YAKKA\s+TARTIBDAGI\s+TADBIRKOR\s*$/i, 'YTT'],
  [/\s*,?\s*ООО\s*$/i, 'ООО'],
  [/^\s*ООО\s+/i, 'ООО'],
  [/\s*,?\s*АО\s*$/i, 'АО'],
];

/** Split a raw org name into a display name + optional legal-form chip. */
export function splitOrgForm(raw: string): { name: string; form: string | null } {
  let s = raw.trim();
  let form: string | null = null;
  for (const [re, label] of ORG_FORMS) {
    if (re.test(s)) {
      const stripped = s.replace(re, '').trim();
      if (stripped) {
        s = stripped;
        form = label;
        break;
      }
    }
  }
  // Drop wrapping quotes left over from names like `"SAPRAMTECH" MChJ`.
  const unquoted = s.replace(/^["'«»`]+|["'«»`]+$/g, '').trim();
  return { name: unquoted || raw, form };
}

export function BuyerGraphPanel({ tenderId }: { tenderId: string }) {
  const t = useT();
  const { locale } = useLocale();
  // NO auth gate: the route is public since 2026-09-18 and answers a guest with
  // the LOCKED payload. A visitor who has not registered is exactly the person
  // the locked card is arguing at, so hiding it from them was backwards.
  const hydrated = useHydrated();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['buyer-graph', tenderId],
    queryFn: () => getBuyerGraph(tenderId),
    // ⚠️ BROWSER ONLY. The prerender renders this component on ~5k tender pages
    // × 2 locales; letting these three queries run there would add ~30k API
    // calls per run — including the buyer-graph union — to a 4-core host that
    // is also serving live traffic. `useHydrated()` is false during SSR and
    // during hydration, so the card is fetched after the page is interactive
    // and never by the prerenderer. (react-query does not fetch in
    // `renderToString` either; this makes that guarantee explicit and greppable
    // instead of relying on it.)
    enabled: hydrated,
  });

  // Russian agrees its nouns with the count, so both the card's rows and the
  // teaser's line pick a form rather than interpolating one: «583 контракта ·
  // 34 поставщика», not the genitive-plural «583 контрактов · 34 поставщиков».
  // uz/kk keys are identical across the three forms, so the index is a no-op.
  const contractsLabel = (n: number) =>
    t(`supplierGraph.contracts.${PLURAL_FORMS[pluralRuIndex(n)]}`, { n });
  const suppliersLabel = (n: number) =>
    t(`supplierGraph.suppliers.${PLURAL_FORMS[pluralRuIndex(n)]}`, { n });

  /** The locked card: the real concentration layout with every figure blurred,
   *  under ONE true line from the server's counts («583 контракта · 34
   *  поставщика в истории заказчика»). Supplier NAMES are never in the teaser —
   *  the rows are «Поставщик № 1..3» — because a name stays real when the blur
   *  is removed. */
  const teaser = (note?: string, suppliers?: number) => (
    <AnalyticsTeaser
      wall="concentration"
      tenderId={tenderId}
      title={t('supplierGraph.title')}
      hint={t('supplierGraph.hint')}
      note={note}
    >
      {/* Kazakhstan only (`honestTeasers`): no more rows than suppliers. */}
      <ConcentrationTeaserBody
        suppliers={countryConfig.features.honestTeasers ? suppliers : undefined}
      />
    </AnalyticsTeaser>
  );

  if (isLoading) {
    return (
      <section
        className="animate-pulse rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
        aria-hidden
      >
        <div className="h-4 w-32 rounded bg-bone" />
        <div className="mt-3 h-16 rounded bg-bone" />
      </section>
    );
  }

  if (isError || !data) return null;

  // Unlocked with data → always show it. The server only sends `graph` to an
  // entitled viewer, so the presence of data is itself the entitlement proof;
  // don't let a missing/false `hasData` existence-flag hide real analytics
  // (robust to an API version that predates the flag).
  const g = !data.locked ? data.graph : null;

  // No renderable data: surface the upgrade CTA only when data exists behind the
  // wall (`locked && hasData`) — never a dead upsell — else render nothing.
  if (!g) {
    if (!(data.locked && data.hasData !== false)) return null;
    const contracts = data.locked ? data.contractCount : undefined;
    const suppliers = data.locked ? data.supplierCount : undefined;
    return teaser(
      contracts && suppliers
        ? t('supplierGraph.teaserBasis', {
            contracts: contractsLabel(contracts),
            suppliers: suppliersLabel(suppliers),
          })
        : undefined,
      suppliers,
    );
  }

  // Plain words that do not shout off a few contracts (Igor, 2026-10-07): the
  // figures always render; 1–4 contracts → «судить рано» (grey), 5–9 → «Похоже,
  // …» and 10+ → the verdict, both in the band's colour (2026-10-10). The index
  // number is never on screen — only in the pill's hover `title`. See
  // `concentrationRead`. `nContracts` counts tenders only — the server leaves
  // direct contracts out.
  const read = concentrationRead(g.hhi, g.nContracts);
  const sentence = concentrationSentence(t, read);
  const pillLevel = concentrationPillLevel(read);

  const top = g.suppliers.slice(0, TOP_N);
  const top3Share = g.suppliers.slice(0, 3).reduce((sum, s) => sum + Math.max(0, s.sharePct), 0);
  const shownShare = top.reduce((sum, s) => sum + Math.max(0, s.sharePct), 0);
  const othersShare = Math.max(0, 100 - shownShare);
  const othersCount = Math.max(0, g.nSuppliers - top.length);

  // Top-N coloured by the same teal ramp as the rank dots below, the rest a
  // neutral tail — the distribution bar and the list are one object.
  const segments: ShareSegment[] = top.map((s, i) => ({
    key: s.tin,
    className: SEGMENT_BG[Math.min(i, SEGMENT_BG.length - 1)]!,
    widthPct: Math.max(0, s.sharePct),
    title: `${splitOrgForm(s.name ?? s.tin).name} · ${s.sharePct.toFixed(1)}%`,
  }));
  if (othersShare > 0.3) {
    segments.push({
      key: 'others',
      className: 'bg-edge',
      widthPct: othersShare,
      title: `${othersCount}`,
    });
  }

  return (
    <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <h3 className="text-[15px] font-bold text-ink">{t('supplierGraph.title')}</h3>
          <InfoHint text={t('supplierGraph.hint')} />
        </div>
        {read.kind === 'verdict' && pillLevel && (
          <ConcentrationPill level={pillLevel} title={hhiTitle(t, g.hhi)}>
            {t(`supplierGraph.concentration.${read.level}`)}
          </ConcentrationPill>
        )}
        {/* Hedged: «Похоже, …» in the same band colour as the 10+ verdict. */}
        {read.kind === 'hedged' && pillLevel && sentence && (
          <ConcentrationPill level={pillLevel} title={hhiTitle(t, g.hhi)}>
            {sentence}
          </ConcentrationPill>
        )}
      </div>
      {read.kind === 'verdict' && (
        <p className="-mt-1 mb-3 text-[12px] text-ink-muted">
          {t(`supplierGraph.concentrationHint.${read.level}`)}
        </p>
      )}
      {read.kind === 'early' && sentence && (
        <p className="-mt-1 mb-3 text-[12px] text-ink-muted">{sentence}</p>
      )}

      {/* Verdict tiles: top-supplier share (with its denominator) and top-3
          share (against the supplier count). Answered before any list. */}
      <div className="mb-4 grid grid-cols-2 gap-2.5">
        <ShareTile
          label={t('supplierGraph.topShare')}
          value={`${g.topSupplierSharePct.toFixed(1)}%`}
          basis={
            g.topSupplierContracts > 0
              ? t('supplierGraph.topShareBasis', {
                  wins: g.topSupplierContracts,
                  total: g.nContracts,
                })
              : undefined
          }
        />
        <ShareTile
          label={t('supplierGraph.top3Share')}
          value={`${top3Share.toFixed(1)}%`}
          basis={t('supplierGraph.suppliersBasis', { n: g.nSuppliers })}
        />
      </div>

      {/* One distribution bar replaces the per-row bars: top-N coloured by the
          same teal ramp as the rank dots below, the rest a neutral tail. */}
      <div className="mb-1 flex items-baseline justify-between gap-2 text-[11px] text-ink-muted">
        <span>{t('supplierGraph.marketShare')}</span>
        <span className="tabular-nums">
          {t('supplierGraph.topNShare', { n: top.length, pct: Math.round(shownShare) })}
        </span>
      </div>
      <MarketShareBar segments={segments} />

      {g.singleSupplierDependence && mayAssertConcentration(g.nContracts) && (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-pill border border-rust/30 bg-orange/10 px-2 py-0.5 text-[11px] font-bold leading-[1.5] text-rust">
          {t('supplierGraph.dependenceFlag')}
        </p>
      )}

      {/* Top suppliers by award value — no per-row bar (the bar above carries
          distribution); a rank dot ties each row to its segment. */}
      <ul className="mt-3.5 flex flex-col">
        {top.map((s, i) => (
          <SupplierRow
            key={s.tin}
            supplier={s}
            rankBg={SEGMENT_BG[Math.min(i, SEGMENT_BG.length - 1)]}
            contractsLabel={contractsLabel}
            locale={locale}
          />
        ))}
      </ul>

      <p className="mt-4 border-t border-edge pt-3 text-[12px] text-ink-muted">
        {t('supplierGraph.footer', {
          suppliers: g.nSuppliers,
          contracts: g.nContracts,
          total: formatBudgetCompact(String(Math.round(g.totalValueUzs)), locale) ?? '0',
        })}
      </p>
      <LocaleLink
        to={`/buyer/${encodeURIComponent(g.buyerTin)}`}
        className="mt-2 inline-block text-[13px] font-semibold text-brand hover:underline"
      >
        {t('buyer.viewProfile')} →
      </LocaleLink>
    </section>
  );
}

function SupplierRow({
  supplier,
  rankBg,
  contractsLabel,
  locale,
}: {
  supplier: SupplierRef;
  rankBg: string;
  contractsLabel: (n: number) => string;
  locale: string;
}) {
  const t = useT();
  const { name, form } = splitOrgForm(supplier.name ?? supplier.tin);

  return (
    <li className="flex items-center gap-2.5 border-t border-edge-light py-2.5 first:border-t-0 first:pt-0.5">
      <span className={`h-2 w-2 shrink-0 rounded-[3px] ${rankBg}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center">
          {/* Only a 9-digit ИНН has a profile page — see isLinkableTin. */}
          {isLinkableTin(supplier.tin) ? (
            <LocaleLink
              to={`/supplier/${supplier.tin}`}
              title={supplier.name ?? supplier.tin}
              className="truncate text-[13.5px] font-semibold text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand"
            >
              {name}
            </LocaleLink>
          ) : (
            <span
              title={supplier.name ?? supplier.tin}
              className="truncate text-[13.5px] font-semibold text-ink"
            >
              {name}
            </span>
          )}
          {form && (
            <span className="ml-1.5 shrink-0 rounded border border-edge-light bg-bone px-1 py-px text-[9.5px] font-bold uppercase tracking-[0.03em] text-ink-muted">
              {form}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-ink-muted">
          {contractsLabel(supplier.contracts)}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[13.5px] font-bold tabular-nums text-ink">
          {supplier.sharePct.toFixed(1)}%
        </div>
        <div className="text-[11px] tabular-nums text-ink-muted">
          {formatBudgetCompact(String(Math.round(supplier.valueUzs)), locale)}{' '}
          {t('tender.budget.unit')}
        </div>
      </div>
    </li>
  );
}
